import { useStorageTableColumns } from "PFApp/components/table/hooks/use_storage_table_columns";
import { useFilteredCollection } from "PFApp/use_filtered_collection";
import { usePage } from "PFApp/use_page";
import { stringify } from "PFCore/helpers/use_deterministic_stringity";
import useStorage from "PFCore/helpers/use_storage";
import { useActivityCounters, useActivityCountersInvalidate } from "PFCore/hooks/queries/workflow_inbox";
import { useEffect, useMemo, useState } from "react";

import { useInbox } from "../use_inbox";
import { useEngagementsColumns } from "./use_engagements_columns";

export const blockedProperties = ["duration"];

const useEngagementsTab = () => {
  const key = "workflow_engagements_v2";

  const { availableColumns } = useEngagementsColumns(blockedProperties);
  const { selectedColumns, setSelectedColumns } = useStorageTableColumns(
    `${key}_columns`,
    availableColumns,
    availableColumns.filter(({ isDefault }) => isDefault)
  );

  const [groupBy, setGroupBy] = useStorage(`${key}_group_by`, null);
  const [pinnedIds, setPinnedIds] = useStorage(`${key}_pinned`, []);

  const [progressView, setProgressView] = useStorage(`${key}_progress_view`, false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const { page, mergePage } = usePage(`workflowPage`);
  const {
    activitiesWithNotifications: engagementsWithNotifications,
    markAsRead,
    isLoading: notificationsLoading
  } = useInbox("engagement");

  const engagements = useFilteredCollection(key, "/api/engagements", {
    requestParams: { filters: { fields: { state: ["new"] } } }
  });
  const { loaded, data, noFiltersSelected, noOrderSelected, fetchData } = engagements;

  const { data: countersData } = useActivityCounters(
    { templateKey: "engagement" },
    { keepPreviousData: true }
  );
  const { invalidate: invalidateCounters } = useActivityCountersInvalidate();

  const stringifiedFilters = stringify(engagements.data.meta.filters);
  useEffect(() => {
    setSelectedIds([]);
  }, [groupBy, stringifiedFilters]);

  const counters = useMemo(
    () => ({
      new: 0,
      draft: 0,
      complete: 0,
      ...(countersData?.stateTally || {})
    }),
    [countersData]
  );

  useEffect(() => {
    mergePage({
      engagements: {
        ...page.engagements,
        ...engagements,
        columns: selectedColumns,
        setColumns: setSelectedColumns,
        fetchData: (params) => {
          fetchData(params);
          invalidateCounters({ templateKey: "engagement" });
        },
        progressView,
        setProgressView,
        groupBy,
        setGroupBy,
        pinnedIds,
        setPinnedIds,
        selectMode,
        setSelectMode,
        selectedIds,
        setSelectedIds,
        unseen: {
          engagements: engagementsWithNotifications,
          loading: notificationsLoading,
          markAsRead
        },
        counters
      }
    });
  }, [
    loaded,
    data,
    counters,
    noFiltersSelected,
    noOrderSelected,
    selectedColumns,
    progressView,
    groupBy,
    pinnedIds,
    selectMode,
    selectedIds,
    engagementsWithNotifications,
    notificationsLoading
  ]);

  return page.engagements;
};

export default useEngagementsTab;
